import React, { useMemo, useState } from 'react';
import { Card, Button, Table, Modal, Form, Badge, Spinner, Row, Col, InputGroup } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';
import { apiService } from '../../services/api';
import { ENV_CONFIG } from '../../config/environment';

const initialFormState = {
  id: null,
  name: '',
  mobile_number: '',
  tss_id: '',
  photo: ''
};

const ParentStaff = () => {
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [photoPreview, setPhotoPreview] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: staffList = [], isLoading } = useQuery({
    queryKey: ['parent_staff'],
    queryFn: async () => {
      const response = await apiService.get('/api/parent/staff');
      return response.data || response;
    }
  });

  const filteredStaff = useMemo(() => {
    if (!searchTerm) return staffList;
    const term = searchTerm.toLowerCase();
    return staffList.filter((staff) =>
      [staff.name, staff.mobile_number, staff.tss_id]
        .filter(Boolean)
        .some((value) => value.toString().toLowerCase().includes(term))
    );
  }, [staffList, searchTerm]);

  const createStaffMutation = useMutation({
    mutationFn: async (payload) => apiService.post('/api/parent/staff', payload),
    onSuccess: (response) => {
      toast.success('Staff member added successfully');
      queryClient.invalidateQueries(['parent_staff']);
      handleCloseModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to add staff member');
    }
  });

  const updateStaffMutation = useMutation({
    mutationFn: async ({ id, data }) => apiService.put(`/api/parent/staff/${id}`, data),
    onSuccess: () => {
      toast.success('Staff member updated successfully');
      queryClient.invalidateQueries(['parent_staff']);
      handleCloseModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update staff member');
    }
  });

  const deleteStaffMutation = useMutation({
    mutationFn: async (id) => apiService.delete(`/api/parent/staff/${id}`),
    onSuccess: () => {
      toast.success('Staff member removed');
      queryClient.invalidateQueries(['parent_staff']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to remove staff member');
    }
  });

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setFormData(initialFormState);
    setPhotoPreview('');
  };

  const handleAddStaff = () => {
    setIsEditing(false);
    setFormData(initialFormState);
    setPhotoPreview('');
    setShowModal(true);
  };

  const handleEditStaff = (staff) => {
    setIsEditing(true);
    setFormData({
      id: staff.id,
      name: staff.name || '',
      mobile_number: staff.mobile_number || '',
      tss_id: staff.tss_id ? staff.tss_id.toString() : '',
      photo: staff.photo || ''
    });
    setPhotoPreview(staff.photo ? getImageUrl(staff.photo) : '');
    setShowModal(true);
  };

  const handleDeleteStaff = (staff) => {
    if (!window.confirm(`Remove ${staff.name}?`)) return;
    deleteStaffMutation.mutate(staff.id);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo must be smaller than 2MB');
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      toast.error('Invalid file type');
      return;
    }

    try {
      setUploadingPhoto(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await apiService.uploadParentStaffPhoto(uploadFormData);
      const photoUrl = response.url || response.data?.url;

      if (!photoUrl) {
        toast.error('Unable to read uploaded photo response');
        return;
      }

      setFormData((prev) => ({ ...prev, photo: photoUrl }));
      setPhotoPreview(getImageUrl(photoUrl));
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const payload = {
      name: formData.name.trim(),
      mobile_number: formData.mobile_number.trim(),
      tss_id: formData.tss_id ? parseInt(formData.tss_id, 10) : null,
      photo: formData.photo || null
    };

    if (!payload.name) {
      toast.error('Name is required');
      return;
    }

    if (!/^[0-9]{10}$/.test(payload.mobile_number)) {
      toast.error('Mobile number must be 10 digits');
      return;
    }

    if (isEditing && formData.id) {
      updateStaffMutation.mutate({ id: formData.id, data: payload });
    } else {
      createStaffMutation.mutate(payload);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const baseUrl = ENV_CONFIG.API_BASE_URL.replace(/\/$/, '');
    return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  };

  const generateIdCard = async (staff) => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [54, 85.6] });

    const barcodeValue = staff.tss_id ? staff.tss_id.toString() : staff.mobile_number || 'PARENT_STAFF';

    const barcodeCanvas = document.createElement('canvas');
    JsBarcode(barcodeCanvas, barcodeValue, { format: 'CODE128', width: 1, height: 20, displayValue: false });
    const barcodeImage = barcodeCanvas.toDataURL('image/png');

    const logo = new Image();
    logo.src = '/logo.png';

    const addPhotoToDoc = async () => {
      if (!staff.photo) return;

      try {
        const imagePath = staff.photo.startsWith('/') ? staff.photo : `/${staff.photo}`;
        const response = await apiService.api.get(`/api/parent/serve_file?path=${encodeURIComponent(imagePath)}`, {
          responseType: 'blob'
        });

        const readerResult = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(response.data);
        });

        doc.addImage(readerResult, 'JPEG', 5.5, 19, 19, 25);
      } catch (error) {
        console.error('Failed to load staff photo for ID card', error);
        try {
          const baseUrl = ENV_CONFIG.API_BASE_URL.replace(/\/$/, '');
          const safePath = staff.photo.startsWith('/') ? staff.photo : `/${staff.photo}`;
          const fallbackResponse = await fetch(`${baseUrl}${safePath}`);
          if (!fallbackResponse.ok) {
            throw new Error('Fallback fetch failed');
          }
          const fallbackBlob = await fallbackResponse.blob();
          const readerResult = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(fallbackBlob);
          });
          doc.addImage(readerResult, 'JPEG', 5.5, 19, 19, 25);
        } catch (fallbackError) {
          console.error('Fallback photo fetch failed', fallbackError);
        }
      }
    };

    logo.onload = async () => {
      doc.setFillColor(102, 126, 234);
      doc.rect(0, 0, 85.6, 15, 'F');

      doc.setFillColor(255, 255, 255);
      doc.circle(9, 7.5, 5.5, 'F');
      try {
        doc.addImage(logo, 'PNG', 5, 3.5, 8, 8);
      } catch (error) {
        console.warn('Failed to draw logo on ID card', error);
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text('The Trivandrum Scottish School', 45, 6, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(5);
      doc.text('PARENT STAFF ID CARD', 45, 12, { align: 'center' });

      doc.setFillColor(255, 255, 255);
      doc.rect(0, 15, 85.6, 34, 'F');

      doc.setDrawColor(102, 126, 234);
      doc.rect(5, 18, 20, 26);

      await addPhotoToDoc();

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text(staff.name || 'N/A', 52, 26, { align: 'center' });

      doc.setFontSize(5.5);
      doc.setFont('helvetica', 'normal');
      doc.text(`Mobile: ${staff.mobile_number || '-'}`, 52, 31, { align: 'center' });
      doc.text(`TSS ID: ${staff.tss_id || 'Pending'}`, 52, 35, { align: 'center' });

      doc.addImage(barcodeImage, 'PNG', 35, 37, 35, 10);

      doc.setFontSize(4.5);
      doc.text('This ID card must be presented on request.', 45, 50, { align: 'center' });

      doc.save(`${staff.name?.replace(/\s+/g, '_') || 'parent_staff'}_id_card.pdf`);
    };

    logo.onerror = () => {
      toast.error('Failed to load school logo for ID card');
    };
  };

  const totalStaff = staffList.length;
  const staffWithPhotos = staffList.filter((staff) => !!staff.photo).length;

  return (
    <div>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h4 className="mb-1">Parent Staff</h4>
          <p className="text-muted mb-0">Manage authorised staff members linked with your account</p>
        </div>
        <div className="d-flex gap-2">
          <InputGroup>
            <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
            <Form.Control
              placeholder="Search by name, mobile, or TSS ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Button variant="primary" onClick={handleAddStaff}>
            <i className="bi bi-plus-circle me-2"></i>
            Add Staff
          </Button>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <small className="text-muted d-block">Total Staff</small>
              <div className="display-6 text-primary mt-2">{totalStaff}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <small className="text-muted d-block">With ID Photo</small>
              <div className="display-6 text-success mt-2">{staffWithPhotos}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <small className="text-muted d-block">Without Photo</small>
              <div className="display-6 text-warning mt-2">{totalStaff - staffWithPhotos}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white">
          <h5 className="mb-0">Authorised Staff Members</h5>
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-people text-muted display-4 d-block mb-3"></i>
              <p className="text-muted">No staff members found. Add one to get started.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>TSS ID</th>
                    <th>Photo</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaff.map((staff, index) => (
                    <tr key={staff.id}>
                      <td><Badge bg="secondary">{index + 1}</Badge></td>
                      <td className="fw-semibold">{staff.name}</td>
                      <td>{staff.mobile_number}</td>
                      <td>{staff.tss_id || <Badge bg="warning" text="dark">Pending</Badge>}</td>
                      <td>
                        {staff.photo ? (
                          <img
                            src={getImageUrl(staff.photo)}
                            alt={staff.name}
                            className="rounded"
                            style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                          />
                        ) : (
                          <span className="text-muted">No photo</span>
                        )}
                      </td>
                      <td className="text-end">
                        <div className="btn-group" role="group">
                          <Button variant="outline-primary" size="sm" onClick={() => generateIdCard(staff)}>
                            <i className="bi bi-credit-card"></i>
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => handleEditStaff(staff)}>
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteStaff(staff)}>
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit Staff Member' : 'Add Staff Member'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="text-muted">Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    maxLength={100}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-muted">Mobile Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.mobile_number}
                    onChange={(e) => handleFormChange('mobile_number', e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                    maxLength={10}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-muted">TSS ID (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.tss_id}
                    onChange={(e) => handleFormChange('tss_id', e.target.value.replace(/[^0-9]/g, ''))}
                    maxLength={6}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="text-muted">Staff Photo</Form.Label>
                  <div className="d-flex align-items-center gap-3">
                    <div>
                      <Button
                        variant="outline-primary"
                        as="label"
                        className="mb-0"
                        disabled={uploadingPhoto}
                      >
                        <i className="bi bi-upload me-2"></i>
                        {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                        <Form.Control type="file" accept="image/*" onChange={handlePhotoUpload} hidden />
                      </Button>
                      <Form.Text className="d-block text-muted mt-1">
                        JPG or PNG, max 2MB
                      </Form.Text>
                    </div>
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="rounded"
                        style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="rounded bg-light d-flex align-items-center justify-content-center"
                        style={{ width: '60px', height: '60px' }}>
                        <i className="bi bi-person text-muted"></i>
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={createStaffMutation.isLoading || updateStaffMutation.isLoading}>
              {isEditing ? 'Save Changes' : 'Add Staff'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ParentStaff;


